import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from "react-redux";
import {compose} from "redux";
import DataGrid, {SelectColumn} from 'react-data-grid';
import Spinner from "../spinner/Spinner";
import CustomSnackbar from "../alert/CustomSnackbar";
import {debounce} from "lodash";
import {
    IsAtBottom
} from "../reporting-table/ReactDataGridUtils";
import {workspaceActions} from "../../store/actions/workspaceAction";
import {Box, Button, Chip, Tooltip} from "@mui/material";
import 'react-data-grid/lib/styles.css';
import {websiteDomainsActions} from "../../store/actions/websiteDomainsAction";
import moment from "moment/moment";
import {useHistory, useLocation} from "react-router-dom";
import {alertActions} from "../../store/actions/alertActions";
import {Add, Delete, Public, Sync} from "@mui/icons-material";

import {WebsiteSectionHeader, WebsiteTableCard, WebsiteEmptyState, WebsiteDeleteDialog} from "./WebsitesDesign";
import {PublicOutlined} from "@mui/icons-material";

function DomainsTable(props) {

    const history = useHistory();
    const location = useLocation();


    const [prompt, setPrompt] = useState(false);
    const [deleteDomainId, setDeleteDomainId] = useState(0);
    const [selectedRows, setSelectedRows] = useState(() => new Set());

    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows,
        getWorkspaceIDs, resetUpdatedWorkspaceValue, verifyDomain, deleteDomainAndCert, alertError
    } = props;
    const {data, isLoading, domain, orderBy, sortBy, sortColumns, rerender} = props?.websiteDomainsState;
    const {plan} = props?.auth?.subscription;

    const getColumns = () => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-domains-columns"));
        let columns = [
            SelectColumn,
            {key: 'id', name: 'ID', hidden: false, width: 70, frozen: false, sortable: true, resizable: true},
            {
                key: 'workspace',
                name: 'Workspace',
                hidden: false,
                width: 105,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'integration',
                name: 'Integration',
                hidden: false,
                width: 115,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'domain',
                name: 'Domain',
                hidden: false,
                width: 200,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'status',
                name: 'Status',
                hidden: false,
                width: 140,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    if (row?.status === "ISSUED") {
                        return <Chip label={row?.status} variant="outlined" color="primary"
                                     sx={{height: "24px", marginTop: 0}}/>
                    } else {
                        return <Chip label={row?.status} variant="outlined" color="warning" sx={{height: "20px"}}/>
                    }
                }
            },
            {
                key: 'created',
                name: 'Created At',
                hidden: false,
                width: 190,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.createdAt === null ? "-" : moment.utc(row?.createdAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
        ];

        if (cachedColumns !== null && cachedColumns?.length !== columns.length) {
            return columns;
        }

        if (cachedColumns !== null && cachedColumns !== "" && cachedColumns !== undefined) {
            let sortedColumns = [];
            cachedColumns?.forEach(cachedCol => {
                columns?.forEach(defCol => {
                    if (cachedCol?.key === defCol?.key) {
                        defCol.width = cachedCol.width;
                        sortedColumns.push(defCol);
                    }
                });
            });
            return sortedColumns;
        }
        localStorage.setItem("website-domains-columns", JSON.stringify(columns));
        return columns;
    };

    const handleDeletePrompt = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                setDeleteDomainId(selectedRowId);
                setPrompt(true);
            }
        }
    };

    const handleClickOnCNAMERecords = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                history.push({
                    pathname: "/websites/domains/" + selectedRowId,
                    state: {background: location}
                });
            }
        }
    };

    const handleDeleteIntegration = (prompt) => {
        if (!prompt) {
            setPrompt(false);
            return false;
        } else {
            deleteDomainAndCert(deleteDomainId);
            setPrompt(false);
        }
    };

    const handleClickOnVerify = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }
        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                verifyDomain(selectedRowId);
            }
        }
    };

    const gridRef = useRef(null);
    const pendingPage = useRef(null);
    const checkedData = useRef(null);

    const [columns, setColumns] = useState(getColumns());

    const onColumnsReorder = (sourceKey, targetKey) => {
        const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
        const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
        const reorderedColumns = [...columns];
        reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
        localStorage.setItem("website-domains-columns", JSON.stringify(reorderedColumns));
        setColumns(reorderedColumns);
    };

    const handleInfinityScroll = useCallback((event) => {
        if (isLoading || pendingPage.current !== null || !IsAtBottom(event)) {
            return;
        }
        const page = Number(data?.page);
        const pageSize = Number(data?.recordsPerPage);
        const total = Number(data?.totalRecords);
        if (page >= 1 && pageSize > 0 && data?.records?.length && data?.recordsReturned !== 0 && page < Math.ceil(total / pageSize)) {
            pendingPage.current = page + 1;
            loadMoreRows(domain, page + 1, orderBy, sortBy, getWorkspaceIDs()).finally(() => {
                pendingPage.current = null;
            });
        }
    }, [isLoading, data, domain, orderBy, sortBy, loadMoreRows, getWorkspaceIDs]);

    // Small pages may not overflow the grid yet, so there is no scroll event to load the next one.
    useEffect(() => {
        if (isLoading || checkedData.current === data) return;
        const frame = requestAnimationFrame(() => {
            const element = gridRef.current?.element;
            if (!element || element.clientHeight === 0) return;
            checkedData.current = data;
            handleInfinityScroll({currentTarget: element});
        });
        return () => cancelAnimationFrame(frame);
    }, [data, isLoading, handleInfinityScroll]);

    const handleGetReport = () => {
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(domain, 1, orderBy, sortBy, getWorkspaceIDs());
    };

    const cacheColumnResize = (idx, width) => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-domains-columns"));
        let modColumns = [];
        cachedColumns?.forEach((item, index) => {
            if (index === idx) {
                item.width = width;
            }
            modColumns.push(item);
        });
        localStorage.setItem("website-domains-columns", JSON.stringify(modColumns));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(cacheColumnResize, 50), []);

    const handleClickOnAddDomain = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        history.push({
            pathname: "/websites/domains/add",
            state: {background: location}
        });
    };

    useEffect(() => {
        handleGetReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getReport, domain, sortBy, orderBy]);

    // Only for rerender
    useEffect(() => {
        if (rerender) {
            handleGetReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            }
        }

        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);

    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            handleGetReport();
            resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated])

    return <Box>
        <WebsiteSectionHeader title="Domains"
                              description="Connect domains and manage verification and free SSL certificates.">
            <Button variant="contained" startIcon={<Add/>} onClick={handleClickOnAddDomain}
                    sx={{minHeight: 40, borderRadius: '8px', px: 2}}>Add domain</Button>
        </WebsiteSectionHeader>
        {prompt && <WebsiteDeleteDialog question="Delete domain?"
                                        text="Are you sure you want to delete this domain and its SSL certificate?"
                                        confirmLabel="Delete domain" confirm={handleDeleteIntegration}/>}
        {alert.errorMsg && <CustomSnackbar message={alert.errorMsg} timeout={8000}/>}
        {alert.successMsg && <CustomSnackbar message={alert.successMsg} severity="success" timeout={8000}/>}
        <WebsiteTableCard title="Domains" count={isLoading ? null : data?.totalRecords ?? data?.records?.length ?? 0}
                          rowCount={data?.records?.length || 0} selectedCount={selectedRows.size} actions={<>
            <Button variant="outlined" color="inherit" startIcon={<Sync/>} disabled={selectedRows.size !== 1}
                    onClick={handleClickOnVerify}>Check status</Button>
            <Button variant="outlined" color="inherit" startIcon={<Public/>} disabled={selectedRows.size !== 1}
                    onClick={handleClickOnCNAMERecords}>CNAME records</Button>
            <Tooltip title="Delete domain"><span><Button variant="outlined" color="error" aria-label="Delete domain"
                                                         disabled={selectedRows.size !== 1} onClick={handleDeletePrompt}
                                                         sx={{minWidth: 35, px: 1}}><Delete fontSize="small"/></Button></span></Tooltip>
        </>}>
            {isLoading && <Spinner size={32} overComponentBox/>}
            {columns !== null && (
                <DataGrid
                    ref={gridRef}
                    rowKeyGetter={(row) => row?.id}
                    columns={columns}
                    onColumnsReorder={onColumnsReorder}
                    rows={data?.records ? data.records : []}
                    defaultColumnOptions={{
                        sortable: true,
                        resizable: true,
                        draggable: true
                    }}
                    onColumnResize={(idx, width) => handleColumnResize(idx, width)}
                    sortColumns={sortColumns}
                    onSortColumnsChange={updateSortColumns}
                    selectedRows={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    onSelectedCellChange={({rowIdx, row, column}) => {
                        if (rowIdx !== -1 && column?.key !== "select-row") {
                            setSelectedRows(new Set().add(row?.id));
                        }
                    }}
                    onScroll={handleInfinityScroll}
                    enableVirtualization={false}
                    className="website-data-grid"
                    rowHeight={55}
                    headerRowHeight={48}
                    aria-label="Domains"
                />
            )}
            {!isLoading && !data?.records?.length && <WebsiteEmptyState icon={PublicOutlined} title="No domains yet"
                                                                        description="Add a domain to verify ownership and set up HTTPS for your website."/>}
        </WebsiteTableCard>
    </Box>;
}

const mapStateToProps = (state) => {
    return {
        websiteDomainsState: state.websiteDomains,
        auth: state.auth,
        alert: state.alert,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: websiteDomainsActions.read,
    loadMoreRows: websiteDomainsActions.readMoreRows,
    updateOrderBy: websiteDomainsActions.updateOrderBy,
    updateSortBy: websiteDomainsActions.updateSortBy,
    updateDomain: websiteDomainsActions.updateDomain,
    updateSortColumns: websiteDomainsActions.updateSortColumns,
    verifyDomain: websiteDomainsActions.verifyDomain,
    getDomainVerifyRecords: websiteDomainsActions.getDomainVerifyRecords,
    deleteDomainAndCert: websiteDomainsActions.deleteDomainAndCert,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue,
    alertError: alertActions.error,
};

export default compose(connect(mapStateToProps, actionCreators))(DomainsTable);
